// Compact side-pane detail view of a scan, used in Gallery master-detail
// mode on lg+ screens. Shows the essentials so the user can review a plant
// without leaving the gallery, with a "View full page" escape hatch.
import React from "react";
import { useTranslation } from "react-i18next";
import { X, ExternalLink, Sun, CloudSun, Moon, Sprout, ShieldAlert, Bug } from "lucide-react";
import { idNative, idEndangered, idStateNote } from "@/lib/idCompat";
import { localizedIdentification } from "@/lib/localizedId";
import { stateCode } from "@/lib/usStates";
import { normalizeSunPreference } from "@/lib/sunPref";
import { useReturnableRoute } from "@/hooks/useReturnableRoute";

const toArray = (v) => Array.isArray(v) ? v : (v == null || v === "" ? [] : [String(v)]);

const SunIcon = ({ pref }) => {
  const p = pref.join(",").toLowerCase();
  if (p.includes("full") && !p.includes("shade")) return <Sun className="w-3.5 h-3.5" strokeWidth={1.5} />;
  if (p.includes("shade") && !p.includes("sun")) return <Moon className="w-3.5 h-3.5" strokeWidth={1.5} />;
  return <CloudSun className="w-3.5 h-3.5" strokeWidth={1.5} />;
};

export default function PlantDetailPane({ scan, onClose, userLang }) {
  const openDetail = useReturnableRoute();
  const { t } = useTranslation();
  const rawId = localizedIdentification(scan, userLang) || {};
  const sunKeys = normalizeSunPreference(rawId.sun_preference);
  const sunLabel = (s) => {
    if (s === "full_sun") return t("result.sunFull");
    if (s === "partial_sun") return t("result.sunPartial");
    if (s === "shade") return t("result.sunShade");
    return s; // last-resort passthrough for unrecognized values
  };
  const id = {
    ...rawId,
    sun_preference: sunKeys,
    keystone_species_support: toArray(rawId.keystone_species_support),
  };
  const needsId = scan?.needs_revisit === true
    || !id.common_name
    || ["unknown", "desconocido"].includes(String(id.common_name).toLowerCase());
  const isNative = idNative(id);
  const isEndangered = idEndangered(id);
  const stateNote = idStateNote(id);

  return (
    <div className="bg-white rounded-3xl border border-[#E8E5DF] overflow-hidden" data-testid={`detail-pane-${scan?.scan_id}`}>
      {/* Header with close */}
      <div className="relative">
        <div className="aspect-[4/3] bg-parchment">
          {(scan?.image_base64 || scan?.thumb_base64) && (
            <img src={scan.image_base64 || scan.thumb_base64} alt={id.common_name || ""} className="w-full h-full object-cover" />
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="detail-pane-close"
          aria-label={t("common.close")}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 border border-[#E8E5DF] text-leaf-ink flex items-center justify-center hover:bg-parchment transition shadow-botanical"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      <div className="p-5 space-y-4">
        <div>
          <p className="font-display text-2xl text-leaf-ink leading-tight">
            {needsId ? t("gallery.card.unidentified") : (id.common_name || t("gallery.card.unknown"))}
          </p>
          {!needsId && id.scientific_name && (
            <p className="italic text-xs text-leaf-mist mt-0.5">{id.scientific_name}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {isNative === true && (
            <span className="badge-pill bg-[#E6EFE9] text-leaf">
              {t("gallery.card.stateNative", { code: stateCode(scan?.state) })}
            </span>
          )}
          {(id.plant_type || "").toLowerCase() === "weed" && (
            <span className="badge-pill bg-[#FDF0E7] text-rust">{t("gallery.card.weed")}</span>
          )}
          {id.is_keystone && (
            <span className="badge-pill bg-[#E9F0D9] text-[#4A5D23]">{t("gallery.card.keystone")}</span>
          )}
          {isEndangered === true && (
            <span className="badge-pill bg-[#FCE9E4] text-rust inline-flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" strokeWidth={1.5} /> {t("share.badge.endangered")}
            </span>
          )}
        </div>

        {id.sun_preference.length > 0 && (
          <Row label={t("result.sun")}>
            <span className="inline-flex items-center gap-1.5 text-sm text-leaf-ink">
              <SunIcon pref={id.sun_preference} /> {id.sun_preference.map(sunLabel).join(" · ")}
            </span>
          </Row>
        )}

        {id.is_keystone && id.keystone_species_support.length > 0 && (
          <Row label={t("share.keystoneFor")} icon={<Sprout className="w-3.5 h-3.5 text-leaf" strokeWidth={1.5} />}>
            <p className="text-sm text-leaf-ink leading-snug">{id.keystone_species_support.join(", ")}</p>
          </Row>
        )}

        {stateNote && (
          <Row label={t("share.stateStatus")}>
            <p className="text-sm text-leaf-ink leading-snug">{stateNote}</p>
          </Row>
        )}

        {id.companion_plant?.name && (
          <Row label={t("share.companion")}>
            <p className="text-sm text-leaf-ink">
              <span className="font-medium">{id.companion_plant.name}</span>
              {id.companion_plant.reason && (
                <span className="block text-xs text-leaf-mist mt-1 leading-relaxed">
                  {id.companion_plant.reason}
                </span>
              )}
            </p>
          </Row>
        )}

        {Array.isArray(id.common_pests) && id.common_pests.length > 0 && (
          <Row label={t("share.pests")} icon={<Bug className="w-3.5 h-3.5 text-leaf-mist" strokeWidth={1.5} />}>
            <ul className="space-y-1.5">
              {id.common_pests.slice(0, 2).map((p, i) => (
                <li key={`${p?.name || "pest"}-${i}`} className="text-xs text-leaf-ink">
                  {p.name && <span className="font-medium">{p.name}</span>}
                  {p.natural_remedy && <span className="block text-leaf mt-0.5">{p.natural_remedy}</span>}
                </li>
              ))}
            </ul>
          </Row>
        )}

        {id.notes && (
          <p className="text-xs text-leaf-ink/80 leading-relaxed pt-3 border-t border-[#F0EDE7]">
            {id.notes}
          </p>
        )}

        <button
          type="button"
          onClick={() => openDetail(`/plant/${scan.scan_id}`, { scan })}
          data-testid="detail-pane-open-full"
          className="w-full rounded-full py-3 bg-leaf text-white hover:bg-leaf-deep transition text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
          {t("gallery.viewFullPage")}
        </button>

        <p className="text-[10px] text-leaf-mist text-center leading-relaxed" data-testid="detail-pane-hint">
          {t("gallery.detailPaneHint")}
        </p>
      </div>
    </div>
  );
}

const Row = ({ label, icon, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.14em] text-leaf-mist mb-1 inline-flex items-center gap-1">
      {icon}{label}
    </p>
    {children}
  </div>
);
